import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import BrandHeader from '../components/BrandHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Post-Stripe-Connect landing page.
 * Poll the Stripe status until it's complete. When complete, issue a magic-link
 * session token automatically (since the user just came back from Stripe and we
 * know it's them) and redirect them into their admin dashboard.
 */
export default function WelcomePage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isRefresh = searchParams.get('refresh') === '1';

  const [club, setClub] = useState(null);
  const [state, setState] = useState('checking'); // checking | stripe_needed | ready
  const [error, setError] = useState('');

  // Disambiguate slug collisions across sports via the URL base path
  const base = window.location.pathname.split('/')[1] || 'worldcup2026';
  const predictorType = base === 'allireland2026' ? 'gaa_final_2026' : base === 'opengolf2026' ? 'golf_open_2026' : 'wc_2026';

  useEffect(() => {
    let cancelled = false;
    let tries = 0;

    const check = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const res = await axios.post(`${API}/clubs/${slug}/stripe-status?predictor_type=${predictorType}`);
        if (cancelled) return;
        setClub(res.data.club);
        if (res.data.stripe_ready) {
          // Auto-issue session token so they skip the login step
          const link = await axios.post(`${API}/clubs/${slug}/grant-owner-session?predictor_type=${predictorType}`);
          if (link.data.token) {
            localStorage.setItem(`club_admin_${slug}`, link.data.token);
            window.location.href = `/${base}/${slug}/admin`;
            return;
          }
        } else {
          setState('stripe_needed');
        }
      } catch (err) {
        if (cancelled) return;
        if (tries < 3) {
          setTimeout(check, 1500);
        } else {
          setError(err.response?.data?.detail || 'Could not check your setup.');
          setState('error');
        }
      }
    };

    check();
    return () => { cancelled = true; };
  }, [slug]);

  const resumeStripe = async () => {
    try {
      const r = await axios.post(`${API}/clubs/${slug}/stripe-resume-link?predictor_type=${predictorType}`);
      if (r.data.url) window.location.href = r.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not resume Stripe setup.');
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <BrandHeader />

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md text-center">
          {state === 'checking' && (
            <div data-testid="welcome-checking">
              <div className="w-12 h-12 border-4 border-[#1D3557]/20 border-t-[#1D3557] rounded-full animate-spin mx-auto mb-6" />
              <h1 className="text-2xl font-black text-[#1D3557] mb-2">Checking your Stripe setup…</h1>
            </div>
          )}

          {state === 'stripe_needed' && (
            <div data-testid="welcome-stripe-needed">
              <h1 className="text-2xl font-black text-[#1D3557] mb-3">
                {isRefresh ? 'Resume Stripe setup' : 'One more step to finish'}
              </h1>
              <p className="text-gray-600 mb-6">
                Your Stripe setup isn't finished yet. Click below to pick up where you left off.
              </p>
              <button
                onClick={resumeStripe}
                className="inline-block w-full py-3.5 bg-[#635BFF] hover:bg-[#4F46E5] text-white font-bold rounded-lg"
                data-testid="resume-stripe-btn"
              >
                Continue with Stripe
              </button>
            </div>
          )}

          {state === 'error' && (
            <div data-testid="welcome-error">
              <h1 className="text-2xl font-bold text-[#1D3557] mb-3">Something went wrong</h1>
              <p className="text-gray-600 mb-4">{error}</p>
              <a href="mailto:support@targetfunder.com" className="inline-block px-6 py-2.5 bg-[#1D3557] text-white font-bold rounded-lg">Contact support</a>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
